import { useState, useEffect, useCallback } from 'react';
import api, { getRooms, createRoom, updateRoom, deleteRoom } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { RoomFormDialog } from '../components/rooms/RoomFormDialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Plus, Pencil, Trash2, BedDouble, Search, Filter, Users, Wifi, Wind, Tv, Coffee } from 'lucide-react';
import { toast } from 'sonner';

const roomStatuses = ['available', 'occupied', 'maintenance'];

const AMENITY_ICONS = {
  wifi: Wifi,
  ac: Wind,
  tv: Tv,
  breakfast: Coffee,
};

const STATUS_THEME = {
  available:  { box: 'border-emerald-300 bg-emerald-50',  icon: 'text-emerald-700', pill: 'bg-emerald-100 text-emerald-700' },
  occupied:   { box: 'border-amber-300 bg-amber-50',      icon: 'text-amber-700',   pill: 'bg-amber-100 text-amber-700' },
  maintenance:{ box: 'border-stone-300 bg-stone-50',      icon: 'text-stone-500',   pill: 'bg-stone-100 text-stone-600' },
};

export default function Rooms() {
  const { isAdmin } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    room_number: '', room_type: 'single', capacity: 1,
    price_per_night: 0, amenities: '', status: 'available', site_category: ''
  });

  const loadRooms = useCallback(async () => {
    try {
      const response = await getRooms(filterStatus || undefined);
      setRooms(response.data);
    } catch {
      toast.error('Failed to load rooms');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { loadRooms(); }, [loadRooms]);

  const [siteCategories, setSiteCategories] = useState([]);
  useEffect(() => { api.get('/site-categories').then((r) => setSiteCategories(r.data)).catch(() => {}); }, []);
  const categoryName = (key) => siteCategories.find((c) => c.room_type === key)?.name;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        amenities: formData.amenities.split(',').map(a => a.trim()).filter(Boolean),
        capacity: parseInt(formData.capacity),
        price_per_night: parseFloat(formData.price_per_night)
      };
      if (editingRoom) {
        await updateRoom(editingRoom.id, data);
        toast.success('Room updated');
      } else {
        await createRoom(data);
        toast.success('Room created');
      }
      setModalOpen(false);
      setEditingRoom(null);
      resetForm();
      loadRooms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (room) => {
    setEditingRoom(room);
    setFormData({
      room_number: room.room_number,
      room_type: room.room_type,
      capacity: room.capacity,
      price_per_night: room.price_per_night,
      amenities: room.amenities?.join(', ') || '',
      status: room.status,
      site_category: room.site_category || ''
    });
    setModalOpen(true);
  };

  const handleDelete = async (room) => {
    if (!window.confirm(`Delete Room ${room.room_number}?`)) return;
    try {
      await deleteRoom(room.id);
      toast.success('Room deleted');
      loadRooms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete room');
    }
  };

  const resetForm = () => setFormData({
    room_number: '', room_type: 'single', capacity: 1,
    price_per_night: 0, amenities: '', status: 'available', site_category: ''
  });

  const openAddModal = () => {
    setEditingRoom(null);
    resetForm();
    setModalOpen(true);
  };

  const filteredRooms = rooms.filter(room =>
    room.room_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
    room.room_type.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group rooms by type for a "dorms-style" cluster view
  const grouped = filteredRooms.reduce((acc, r) => {
    const key = r.room_type;
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});
  const typeOrder = ['single', 'double', 'suite', 'deluxe', 'dormitory'];
  const orderedTypes = [
    ...typeOrder.filter(t => grouped[t]),
    ...Object.keys(grouped).filter(t => !typeOrder.includes(t)),
  ];

  return (
    <div data-testid="rooms-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 font-['Manrope'] tracking-tight">Rooms</h1>
          <p className="text-stone-500 mt-1">Tap a room card to edit. Status colours match the dorm-bed view.</p>
        </div>
        <Button
          onClick={openAddModal}
          className="bg-sage-700 hover:bg-sage-800 text-white rounded-sm"
          data-testid="add-room-button"
        >
          <Plus className="w-4 h-4 mr-2" />Add Room
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input
            placeholder="Search rooms..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 border-stone-300"
            data-testid="search-rooms-input"
          />
        </div>
        <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-40 border-stone-300" data-testid="filter-status-select">
            <Filter className="w-4 h-4 mr-2 text-stone-400" />
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Status</SelectItem>
            {roomStatuses.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mb-4 text-xs text-stone-500">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border border-emerald-300 bg-emerald-50" />Available</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border border-amber-300 bg-amber-50" />Occupied</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border border-stone-300 bg-stone-50" />Maintenance</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage-700"></div>
        </div>
      ) : filteredRooms.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-md p-12 text-center">
          <BedDouble className="w-12 h-12 mx-auto mb-3 text-stone-300" />
          <p className="text-stone-500 mb-4">No rooms found</p>
          <Button onClick={openAddModal} variant="outline" className="border-stone-300">
            Add your first room
          </Button>
        </div>
      ) : (
        orderedTypes.map((type) => {
          const list = grouped[type];
          const availCount = list.filter(r => r.status === 'available').length;
          return (
            <div key={type} className="bg-white border border-stone-200 rounded-md mb-4">
              <div className="p-4 border-b border-stone-200 bg-stone-50 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-stone-900 font-['Manrope'] capitalize">{type} Rooms</h3>
                  <p className="text-sm text-stone-500">{list.length} total • {availCount} available</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 p-4">
                {list.map((room, index) => {
                  const theme = STATUS_THEME[room.status] || STATUS_THEME.maintenance;
                  const amenities = room.amenities || [];
                  return (
                    <button
                      key={room.id}
                      onClick={() => handleEdit(room)}
                      className={`group text-left border rounded-md p-3 relative transition-all hover:shadow-md hover:-translate-y-0.5 animate-fade-in ${theme.box}`}
                      style={{ animationDelay: `${index * 0.03}s` }}
                      data-testid={`room-card-${room.id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <BedDouble className={`w-6 h-6 ${theme.icon}`} />
                        <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${theme.pill}`}>{room.status}</span>
                      </div>
                      <p className="font-semibold text-stone-900 font-['Manrope'] text-lg leading-tight">{room.room_number}</p>
                      <p className="text-xs text-stone-500 capitalize mt-0.5">{room.room_type}</p>
                      {room.site_category && <p className="text-[10px] text-sage-700 mt-0.5 truncate" title="Website category" data-testid={`room-site-category-${room.id}`}>Web: {categoryName(room.site_category) || room.site_category}</p>}
                      <div className="flex items-center gap-2 mt-2 text-xs text-stone-600">
                        <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{room.capacity}</span>
                        <span className="font-medium text-sage-700">₹{room.price_per_night}</span>
                      </div>
                      {amenities.length > 0 && (
                        <div className="flex items-center gap-1 mt-2 flex-wrap">
                          {amenities.slice(0, 4).map((a, i) => {
                            const Icon = AMENITY_ICONS[a.toLowerCase()];
                            return Icon ? (
                              <Icon key={i} className="w-3 h-3 text-stone-400" title={a} />
                            ) : (
                              <span key={i} className="text-[10px] text-stone-400">·{a}</span>
                            );
                          })}
                          {amenities.length > 4 && <span className="text-[10px] text-stone-400">+{amenities.length - 4}</span>}
                        </div>
                      )}
                      {isAdmin && (
                        <span
                          role="button"
                          aria-label="Delete room"
                          onClick={(e) => { e.stopPropagation(); handleDelete(room); }}
                          className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition text-stone-400 hover:text-red-600 p-1"
                          data-testid={`delete-room-${room.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </span>
                      )}
                      <span className="absolute bottom-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition text-stone-400">
                        <Pencil className="w-3 h-3" />
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })
      )}

      <RoomFormDialog
        open={modalOpen}
        onOpenChange={setModalOpen}
        onSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        editingRoom={editingRoom}
        siteCategories={siteCategories}
      />
    </div>
  );
}
